import { truncateMetaDescription } from '@/lib/category-seo';
import { stripHtmlForMeta } from '@/lib/seo';

const SITE_NAME = 'Turkish E-Market';
const TITLE_SUFFIX = ` | ${SITE_NAME}`;
const MAX_TITLE_LEN = 60;
const MAX_DESC_LEN = 158;

const META_DESC_CTA = {
  tr: "Turkish E-Market'ten güvenle sipariş verin, hızlı teslimat avantajından yararlanın.",
  en: 'Order securely from Turkish E-Market with fast delivery.',
  de: 'Sicher bei Turkish E-Market bestellen — schnelle Lieferung.',
  fr: 'Commandez en toute sécurité sur Turkish E-Market, livraison rapide.',
  es: 'Compre con seguridad en Turkish E-Market, entrega rápida.',
  ar: 'اطلب بأمان من Turkish E-Market مع توصيل سريع.',
  ru: 'Закажите на Turkish E-Market с быстрой доставкой.',
};

function normalizeLang(lang) {
  return String(lang || 'tr').split('-')[0].slice(0, 2).toLowerCase() || 'tr';
}

function getMetaDescCta(lang) {
  const code = normalizeLang(lang);
  return META_DESC_CTA[code] || META_DESC_CTA.en;
}

/** İlk cümle veya anlamlı özet (meta description gövdesi) */
function excerptForMeta(text, max = 100) {
  if (!text) return '';
  const cleaned = text.trim();
  const sentence = cleaned.match(/^[^.!?…]+[.!?…]/);
  if (sentence && sentence[0].length <= max) return sentence[0].trim();
  if (cleaned.length <= max) return cleaned;
  const cut = cleaned.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  const base = lastSpace > 40 ? cut.slice(0, lastSpace) : cut.slice(0, max - 1);
  return `${base.trim()}…`;
}

/**
 * Otomatik meta title: "Ürün Adı | Turkish E-Market" (~50–60 karakter)
 */
export function buildProductSeoTitle(product) {
  const name = (product?.name || '').trim();
  if (!name) return SITE_NAME;
  const full = `${name}${TITLE_SUFFIX}`;
  if (full.length <= MAX_TITLE_LEN) return full;
  const maxNameLen = MAX_TITLE_LEN - TITLE_SUFFIX.length - 1;
  return `${name.slice(0, maxNameLen).trim()}…${TITLE_SUFFIX}`;
}

/**
 * Otomatik meta description: açıklama özeti + CTA (~150–158 karakter)
 */
export function buildProductSeoDescription(product, lang = 'tr') {
  const name = (product?.name || '').trim();
  const cta = getMetaDescCta(lang);
  const descRaw = stripHtmlForMeta(
    product?.description || product?.short_description || '',
    400,
  );

  const roomForLead = MAX_DESC_LEN - cta.length - 1;
  let lead = excerptForMeta(descRaw, Math.min(110, Math.max(50, roomForLead - 5)));

  if (!lead || lead.length < 20) {
    lead = name ? `${name}.` : '';
  } else if (!/[.!?…]$/.test(lead)) {
    lead = `${lead}.`;
  }

  const combined = `${lead} ${cta}`.replace(/\s+/g, ' ').trim();
  return truncateMetaDescription(combined, MAX_DESC_LEN);
}

/** Admin seo_title veya otomatik başlık */
export function resolveProductSeoTitle(product) {
  const custom = product?.seo_title?.trim();
  if (custom) return custom;
  return buildProductSeoTitle(product);
}

/** Admin seo_description veya otomatik açıklama */
export function resolveProductSeoDescription(product, lang = 'tr') {
  const custom = product?.seo_description?.trim();
  if (custom) return truncateMetaDescription(custom, MAX_DESC_LEN);
  return buildProductSeoDescription(product, lang);
}
